import React, { Component } from 'react';
import Quagga from '@ericblade/quagga2';
import jsQR from 'jsqr';
import WebcamComponent from 'react-webcam';

import Select from '#comp/Custom/Select';
import { Button } from 'flowbite-react';

import Context from '#context';
import {
  DEFAULT_CAMERA,
  cameraConstraints,
  getMediaDevices,
  reportCameraError,
} from '#helper/camera';

function getMedian(arr) {
  const newArr = [...arr];
  newArr.sort((a, b) => a - b);
  const half = Math.floor(newArr.length / 2);
  if (newArr.length % 2 === 1) {
    return newArr[half];
  }
  return (newArr[half - 1] + newArr[half]) / 2;
}

function getMedianOfCodeErrors(decodedCodes) {
  const errors = decodedCodes.flatMap((x) => x.error);
  const medianOfErrors = getMedian(errors);
  return medianOfErrors;
}

const defaultConstraints = {
  width: 800,
  height: 600,
};

const QR_CHECK_INTERVAL = 100;
const MAX_QR_ERROR = 0.20;

const defaultLocatorSettings = {
  patchSize: 'medium',
  halfSample: true,
};

const defaultDecoders = ['ean_reader', 'code_128_reader'];

export class QRScanner extends Component {
  static contextType = Context;

  constructor(props) {
    super(props);
    this.scannerRef = React.createRef();
    this.state = {
      torchOn: false,
      zoom: 1,
    };
  }

  componentDidMount = async () => {
    const constraints = {
      ...defaultConstraints,
      ...cameraConstraints(this.props.deviceid),
    };
    try {
      await Quagga.init(
        {
          inputStream: {
            type: 'LiveStream',
            constraints,
            target: this.scannerRef.current,
            willReadFrequently: true,
          },
          locator: defaultLocatorSettings,
          decoder: { readers: defaultDecoders },
          locate: true,
        },
        (err) => {
          if (err) {
            reportCameraError(this.context.t, err, 'QRScanner', { constraints });
            return;
          }
          
          Quagga.start();
          
          // Make sure all canvas elements have the correct size
          // We use requestAnimationFrame to ensure the canvas elements are rendered
          requestAnimationFrame(() => {
            const container = this.scannerRef.current;
            if (!container) return;
            
            const containerRect = container.getBoundingClientRect();
            const canvasWidth = Math.min(defaultConstraints.width, Math.floor(containerRect.width));
            const canvasHeight = Math.min(defaultConstraints.height, Math.floor(containerRect.height));
            
            const canvases = container.querySelectorAll('canvas');
            canvases.forEach((canvas) => {
              // Set the internal dimensions based on the container size
              canvas.width = canvasWidth;
              canvas.height = canvasHeight;
              
              // CSS makes it responsive
              canvas.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                border: none;
                display: block;
              `;
            });
			window.scrollTo({ top: document.body.scrollHeight });
          });
        }
      );
    } catch {
    }

    // Register event listeners
    Quagga.onDetected(this.handleDetected);
    Quagga.onProcessed(this.handleProcessed);
  };

  handleDetected = (result) => {
    try {
      if (!result || !result.codeResult) return;

      // Error-Check: only accept if Quagga is at least MAX_QR_ERROR sure
      const err = getMedianOfCodeErrors(
        result.codeResult.decodedCodes
      );
      if (err > 0 && err < MAX_QR_ERROR) {
        const barcode = result.codeResult.code;
        this.props.resultScanner(barcode);
      }
    } catch {
      // Error handling
    }
  };

  handleProcessed = (result) => {
    if (!result) return;

    const now = Date.now();

    // Try to scan the QR-Code
    if (!this.lastQRAttempt) this.lastQRAttempt = 0;
    
    if (now - this.lastQRAttempt > QR_CHECK_INTERVAL) {
      this.lastQRAttempt = now;
      
      try {
        // Use Quagga's internal Canvas or Video
        const canvas = Quagga.canvas.dom.image || 
                      Quagga.canvas.dom.video || 
                      document.querySelector('video');
        
        if (!canvas) {
          return;
        }

        // Create a temporary Canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = defaultConstraints.width;
        tempCanvas.height = defaultConstraints.height;
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
        
        // Draw the Video/Canvas on the temp Canvas
        if (canvas instanceof HTMLVideoElement) {
          tempCtx.drawImage(canvas, 0, 0, defaultConstraints.width, defaultConstraints.height);
        } else if (canvas instanceof HTMLCanvasElement) {
          tempCtx.drawImage(canvas, 0, 0, defaultConstraints.width, defaultConstraints.height);
        }
        
        // Get the Image Data
        const imageData = tempCtx.getImageData(0, 0, defaultConstraints.width, defaultConstraints.height);
        
        if (!imageData || !imageData.data) {
          return;
        }
        
        // Try to recognize the QR-Code
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (qrCode && qrCode.data) {
          this.props.resultScanner(qrCode.data);
          return;
        }
      } catch {
        // No Error
      }
    }
  };

  enableTorch = async () => {
    try {
      await Quagga.CameraAccess.enableTorch();
      this.setState({ torchOn: true });
    } catch {
		// No Error
    }
  };

  disableTorch = async () => {
    try {
      await Quagga.CameraAccess.disableTorch();
      this.setState({ torchOn: false });
    } catch {
		// No Error
    }
  };

  toggleTorch = async () => {
    if (this.state.torchOn) {
      await this.disableTorch();
    } else {
      await this.enableTorch();
    }
  };

  setZoom = async (zoomLevel) => {
    try {
      const track = Quagga.CameraAccess.getActiveTrack();
      if (track) {
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          await track.applyConstraints({
            advanced: [{ zoom: zoomLevel }],
          });
          this.setState({ zoom: zoomLevel });
        }
      }
    } catch {
		// No Error
    }
  };

  componentWillUnmount = () => {
    Quagga.stop();
  };

  render() {
    const { torchOn, zoom } = this.state;
	const { t } = this.context;

    return (
      <div className="flex flex-col gap-4">

		<div className="flex gap-2 justify-center">
		<Button
			onClick={this.toggleTorch}
			color={torchOn ? 'warning' : 'gray'}
		>
			{torchOn ? t('cam.startTorch') : t('cam.stopTorch')}
		</Button>

		<div className="flex items-center gap-2">
			<label htmlFor="zoom-slider">{t('cam.zoom')}:</label>
			<input
				id="zoom-slider"
				type="range"
				min="1"
				max="5"
				step="0.5"
				value={zoom}
				onChange={(e) => this.setZoom(parseFloat(e.target.value))}
				className="w-32"
			/>
			<span>{zoom.toFixed(1)}x</span>
		</div>
		</div>

        <div className="flex justify-center">
          <div
            ref={this.scannerRef}
            className="rounded-lg relative shadow-lg overflow-hidden w-full"
          />
        </div>
      </div>
    );
  }
}

export class Selector extends Component {
  static contextType = Context;

  state = {
    videoDevices: [],
    loading: true,
  };

  componentDidMount() {
    this.loadCameras();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  loadCameras = async () => {
    this.setState({ loading: true });
    const videoDevices = await getMediaDevices(this.context.t);
    if (this.unmounted) return;
    this.setState({ videoDevices, loading: false });

    // A saved camera that no longer exists would silently open another one.
    const { selectedDeviceId, changeSelected } = this.props;
    if (
      videoDevices.length > 0 &&
      selectedDeviceId !== DEFAULT_CAMERA &&
      !videoDevices.some((device) => device.deviceId === selectedDeviceId)
    ) {
      changeSelected(DEFAULT_CAMERA);
    }
  };

  render() {
    const { selectedDeviceId, startCam, startScanner, changeSelected } =
      this.props;
    const { videoDevices, loading } = this.state;
    const { t } = this.context;

    return (
      <div className="mb-2">
        <div className="flex gap-2 xs:flex-row flex-col">
          <Select
            defaultValue={selectedDeviceId || DEFAULT_CAMERA}
            onChange={(e) => changeSelected(e.target.value)}
            name="selectCamera"
            label={t('cam.labelCamSelector')}
            className="grow"
          >
            <option value={DEFAULT_CAMERA}>
              {t('cam.defaultCamSelector')}
            </option>
            {videoDevices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || device.deviceId}
              </option>
            ))}
          </Select>
          <div className="flex items-center flex-col xs:flex-row gap-2">
            <Button
              className="w-full xs:w-auto"
              disabled={loading}
              onClick={startScanner.bind(this)}
            >
              {startCam ? t('cam.stopCam') : t('cam.startCam')}
            </Button>
          </div>
        </div>
        {!loading && videoDevices.length === 0 && (
          <div
            role="status"
            className="mt-2 flex flex-col xs:flex-row xs:items-center gap-2 text-sm"
          >
            <p className="grow">{t('cam.noList')}</p>
            <Button
              className="w-full xs:w-auto shrink-0"
              color="gray"
              onClick={this.loadCameras}
            >
              {t('cam.retryList')}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default class Webcam extends Component {
	static contextType = Context

	constructor(props) {
		super(props)
		this.webcamRef = React.createRef()
	}

	// Without this the page stays empty and nobody learns why the camera did not open.
	handleError = (error) => {
		// react-webcam passes a string when the browser has no getUserMedia.
		const cameraError = typeof error === 'string' ? new Error(error) : error
		reportCameraError(this.context.t, cameraError, 'Webcam', {
			constraints: cameraConstraints(this.props.deviceId),
		})
		this.props.onError?.()
	}

	setRef = (webcam) => {
		this.webcamRef = webcam
	}

	captureImage = () => {
		const imageSrc = this.webcamRef.getScreenshot()

		this.props.tookImage(imageSrc)
	}

	handleVideoLoaded = () => {
		window.scrollTo({ top: document.body.scrollHeight })
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.takePicture !== this.props.takePicture &&
			this.props.takePicture === true
		) {
			this.captureImage()
		}
	}

	render() {
		return (
			<div className="flex justify-center">
				<WebcamComponent
					className="rounded-lg"
					videoConstraints={cameraConstraints(this.props.deviceId)}
					muted
					width={800}
					height={600}
					audio={false}
					ref={this.setRef}
					screenshotFormat="image/jpeg"
					onLoadedMetadata={this.handleVideoLoaded}
					onUserMediaError={this.handleError}
				/>
			</div>
		)
	}
}